import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  VStack,
} from '@chakra-ui/react';
import { accountApi } from '@netiva/classifieds-api';
import { usePropChange } from '@netiva/classifieds-common';
import { CodeTextarea, FormInput } from '@netiva/classifieds-ui';

type CreateApiKeyModalProps = Omit<ModalProps, 'children'>;

type FormValues = {
  name?: string;
};

export const CreateApiKeyModal: FC<CreateApiKeyModalProps> = (props) => {
  const { t } = useTranslation();

  const [createApiKey, { isLoading }] = accountApi.useCreateApiKey();
  const [apiKey, setApiKey] = useState('');

  const form = useForm<FormValues>({
    defaultValues: {
      name: '',
    },
  });

  const { reset } = form;

  if (usePropChange(props.isOpen)) {
    reset();
    setApiKey('');
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      const response = await createApiKey({ accountApiKeysCreateApiKeyRequest: { name: values.name } }).unwrap();
      setApiKey(response.key);
    } catch (err) {
      console.error(err);
      // TODO
    }
  };

  return (
    <Modal isCentered size={['full', 'lg']} {...props}>
      <ModalOverlay />
      <ModalContent>
        <FormProvider {...form}>
          <form {...form} onSubmit={form.handleSubmit(handleSubmit)}>
            <ModalHeader>
              <Text>{t('api.apiKeys.create')}</Text>
              <ModalCloseButton />
            </ModalHeader>
            <ModalBody>
              <VStack align="stretch" spacing={8}>
                <FormInput<FormValues>
                  name="name"
                  label={t('api.apiKeys.createModal.name.label')}
                  description={t('api.apiKeys.createModal.name.description')}
                />

                <Flex justify="center">
                  <Button type="submit" isLoading={isLoading} isDisabled={!!apiKey}>
                    {t('api.apiKeys.createModal.submit')}
                  </Button>
                </Flex>

                <FormControl>
                  <FormLabel>{t('api.apiKeys.createModal.apiKey.label')}</FormLabel>
                  <CodeTextarea value={apiKey} />
                  <FormHelperText color="red.400" fontSize="md">
                    {t('api.apiKeys.createModal.apiKey.description')}
                  </FormHelperText>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter justifyContent="center">
              <Button onClick={props.onClose} colorScheme="neutral">
                {t('ui.buttons.close')}
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};
